'use client'

export default function PrivacyPolicyPage() {
  return (
    <div className="container max-w-4xl mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
      <div className="prose prose-gray dark:prose-invert max-w-none">
        <p className="text-lg mb-8">
          Last updated: February 2, 2026
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
          <p>
            GovCon Data ("Company", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website ([Your Website URL]), use our platform, or interact with our services (collectively, the "Service"). Please read this policy carefully. If you do not agree with the terms of this privacy policy, please do not access or use the Service.
          </p>
          <p>
            We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of this Privacy Policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
          <p className="mb-4">We may collect information about you in a variety of ways. The information we may collect via the Service includes:</p>
          
          <h3 className="text-xl font-semibold mb-3">2.1 Personal Data</h3>
          <p className="mb-4">
            Personally identifiable information, such as your name, email address, phone number, company name, job title, and any other information you voluntarily provide to us when you register for an account, subscribe to our services, contact us, or interact with certain features.
          </p>
          <p className="mb-4">
            If you subscribe to paid tiers, we collect payment information (like credit card details), which is processed securely by our third-party payment processor (Stripe). We do not store your full payment card details.
          </p>

          <h3 className="text-xl font-semibold mb-3">2.2 Usage Data</h3>
          <p className="mb-4">
            Information our servers automatically collect when you access the Service, such as your IP address, browser type, operating system, access times, pages viewed, search queries, feature interactions, and device information.
          </p>

          <h3 className="text-xl font-semibold mb-3">2.3 Cookies and Tracking Technologies</h3>
          <p className="mb-4">
            We use cookies, web beacons, tracking pixels, and other tracking technologies to help customize the Service and improve your experience. For more information on how we use cookies, please refer to our <a href="/cookie-policy" className="text-primary hover:underline">Cookie Policy</a>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
          <p className="mb-4">Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Service to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Create and manage your account.</li>
            <li>Provide, operate, and maintain the Service.</li>
            <li>Process your subscriptions, payments, and other transactions.</li>
            <li>Respond to your comments, questions, and provide customer support.</li>
            <li>Send you technical notices, updates, security alerts, and administrative messages.</li>
            <li>Communicate with you about products, services, offers, promotions, and events offered by us, and provide news and information we think will be of interest to you (you may opt-out of marketing communications).</li>
            <li>Monitor and analyze trends, usage, and activities in connection with our Service to improve the Service.</li>
            <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities and protect the rights and property of GovCon Data and others.</li>
            <li>Personalize and improve the Service and provide content or features that match user profiles or interests.</li>
            <li>Comply with legal and regulatory requirements.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. How We Share Your Information</h2>
          <p className="mb-4">We may share information we have collected about you in certain situations. Your information may be disclosed as follows:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>By Law or to Protect Rights:</strong> If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
            </li>
            <li>
              <strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us or on our behalf, including payment processing (e.g., Stripe), data analysis, email delivery, hosting services, customer service, cloud infrastructure (e.g., AWS), and marketing assistance.
            </li>
            <li>
              <strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
            </li>
            <li>
              <strong>With Your Consent:</strong> We may disclose your personal information for any other purpose with your consent.
            </li>
          </ul>
          <p className="mt-4">We do not sell your personal information.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. AI Data Processing and Confidentiality</h2>
          <p className="mb-4">
            Our Service utilizes artificial intelligence (AI) to provide certain features. We are committed to maintaining the confidentiality and security of any data you provide for AI processing.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Data processed by our AI systems is used solely to provide the requested service to you.</li>
            <li>We implement strict technical and organizational measures to ensure data isolation between customers.</li>
            <li>Your proprietary data is not used to train general AI models or models for other customers.</li>
            <li>All data is encrypted at rest and in transit during AI processing.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. Data Security</h2>
          <p>
            We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. Data Retention</h2>
          <p>
            We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy, including for the purposes of satisfying any legal, accounting, or reporting requirements. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. Your Data Protection Rights</h2>
          <p className="mb-4">Depending on your location, you may have certain rights regarding your personal information under applicable data protection laws (such as GDPR or CCPA). These may include the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Request access to and receive a copy of your personal information.</li>
            <li>Request correction of any inaccurate or incomplete personal information.</li>
            <li>Request erasure of your personal information under certain conditions.</li>
            <li>Object to or restrict the processing of your personal information under certain conditions.</li>
            <li>Request the transfer of your personal information to another organization, or directly to you, under certain conditions (data portability).</li>
            <li>Withdraw your consent at any time where we rely on consent to process your personal information.</li>
            <li>Lodge a complaint with a data protection authority.</li>
          </ul>
          <p className="mt-4">To exercise any of these rights, please contact us using the contact information provided below.</p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">9. International Data Transfers</h2>
          <p>
            Your information, including personal data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction. If you are located outside the United States and choose to provide information to us, please note that we transfer the data, including Personal Data, to the United States and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
          <p>
            Our Service is not intended for use by children under the age of 13 (or 16 in certain jurisdictions). We do not knowingly collect personally identifiable information from children under 13. If we become aware that we have collected Personal Data from a child under the relevant age without verification of parental consent, we take steps to remove that information from our servers.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
          <p>
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:{' '}
            <a href="mailto:info@govcon-data.com" className="text-primary hover:underline">
              info@govcon-data.com
            </a>
          </p>
        </section>
      </div>
    </div>
  )
} 